import { AlertDialogAction } from '@radix-ui/react-alert-dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
} from 'primitives/alert-dialog'
import { Button, type ButtonProps, ButtonWithLoader } from 'primitives/button'
import { useRef } from 'react'

export interface ConfirmPopupProps {
  okButtonProps?: ButtonProps & { btnTxt?: string }
  cancelButtonProps?: ButtonProps & { btnTxt?: string }
  children?: React.ReactNode
  title?: string
  description?: string | React.ReactNode
  content?: React.ReactNode
  open?: boolean
  setOpen?: (open: boolean) => void
}

export const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
  okButtonProps,
  cancelButtonProps,
  children,
  title,
  description,
  content,
  open,
  setOpen,
}) => {
  const activeElement = useRef<HTMLElement>() // to store the active element before the popup is opened
  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2187447622
        setTimeout(() => {
          document.body.style.pointerEvents = ''
        }, 200)
        setOpen?.(o)
      }}
    >
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent
        onOpenAutoFocus={() => {
          activeElement.current = document.activeElement as HTMLElement
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          activeElement.current?.focus()
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription style={{ wordBreak: 'break-word' }}>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {content}
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline" {...cancelButtonProps}>
              {cancelButtonProps?.btnTxt || 'Cancel'}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button {...okButtonProps}>{okButtonProps?.btnTxt || 'Confirm'}</Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

type ButtonWithLoaderProps = ButtonProps & { loading: boolean }
export const DestruciveConfirmPopup: React.FC<ConfirmPopupProps & ButtonWithLoaderProps> = ({
  children,
  ...props
}) => (
  <ConfirmPopup {...props} okButtonProps={{ variant: 'destructive', ...props.okButtonProps }}>
    <ButtonWithLoader variant="destructive-outline" {...props}>
      {children}
    </ButtonWithLoader>
  </ConfirmPopup>
)
